/**
 * Inline preview of the document the patient is about to sign (PF-6119).
 *
 * The open-in-new-tab link is NOT a fallback that appears on failure — it is always visible. iOS
 * Safari silently refuses to render a PDF inside an embedded `<object>`, and these patients are on
 * phones, so a link that only shows when the embed reports an error would never show at all.
 */
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { radii } from '../../../styles/onboardingUiTokens';
import { Notice } from '../ui';
import { signatureRecaptureCopy } from '../config/copy';

type DocumentPreviewProps = {
  url: string;
  name: string;
};

export function DocumentPreview({ url, name }: DocumentPreviewProps) {
  return (
    <>
      <DocumentFrame data={url} type="application/pdf" aria-label={name}>
        <FallbackNotice preset="info">{signatureRecaptureCopy.sign.documentFallbackNotice}</FallbackNotice>
      </DocumentFrame>
      <FallbackLink href={url} target="_blank" rel="noreferrer">
        {signatureRecaptureCopy.sign.documentFallbackLink}
      </FallbackLink>
    </>
  );
}

const DocumentFrame = styled.object`
  width: 100%;
  height: 60vh;
  min-height: 320px;
  border: 1px solid ${colors.light};
  border-radius: ${radii.lg};
  background: ${colors.white};
`;

const FallbackNotice = styled(Notice)`
  height: 100%;
`;

const FallbackLink = styled.a`
  display: block;
  text-align: center;
  color: ${colors.primary};
  font-weight: 600;
  text-decoration: underline;
`;
